import { useState, Dispatch, SetStateAction, SyntheticEvent } from 'react';
import { insertTodo } from '../models/todoModel';
import { insertCategory } from '../models/categoryModel';
import type { Todo, Category } from '../types/index';

export default function TodoForm({ todos, setTodos, categoryList, setCategoryList, setLastAddedId, inputSelect, setInputSelect }: {
  todos: Todo[],
  setTodos: (value: Todo[]) => void,
  categoryList: Category[],
  setCategoryList: Dispatch<SetStateAction<Category[]>>,
  setLastAddedId: (value: number | null) => void,
  inputSelect: number,
  setInputSelect: Dispatch<SetStateAction<number>>
}) {
  const [inputText, setInputText] = useState("");
  const [inputError, setInputError] = useState(0);

  // フォームの送信ボタンが押されたときの動作
  // supbaseを利用するにあたりhandleSubmitもasync/awaitに変更が必要
  // SyntheticEventはReactのイベントオブジェクトの型。FormEventもあるが非推奨で、代わりにSyntheticEventを使うのが公式の推奨
  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    // addTodoContentの戻り値として新しく追加したTodoのIDが返ってくる
    // supbaseのデータを待つので、addTodoContentの呼び出しにawaitが必要
    const newId = await addTodoContent(todos, setTodos, inputText, setInputText, inputSelect, setInputError);

    // 追加した瞬間にblinkクラスを付けて、2秒後に外す挙動
    if (newId) {
      // 追加したTodoのIDをstateに保存します。これがblinkクラスを付ける条件になります。
      setLastAddedId(newId);
      // 2b000ミリ秒後にlastAddedIdをnullに戻して、blinkクラスを外します。
      setTimeout(() => setLastAddedId(null), 1000);
    }
  };
  return (
    <>
      {/* Enterで登録できる仕様にするには、formタグとonSubmit指定する。*/}
      <form id="TodoForm" className="todo-form" onSubmit={handleSubmit}>
        <div className="todo-category">
          <TodoFormCategorySelect categoryList={categoryList} inputSelect={inputSelect} setInputSelect={setInputSelect} />
          {/*呼び出し側のボタンも async に変える*/}
          <button className="category-button" type="button" onClick={async () => {
            await AddCategory(setCategoryList, setInputSelect);
          }}
          >カテゴリーを追加する</button>
        </div>
        <input type="text" id="TodoInputText" name="TodoInputText" value={inputText} placeholder="やることを入力してください"
          className="todo-form-input" onChange={(e) => setInputText(e.target.value)} />
        {/* button type="submit の動作は、formタグのonSubmitで指定する*/}
        <button type="submit" className="todo-form-submit">追加</button>
      </form>
      {inputError === 1 && <p className="todo-form-error   mt-6 text-red-500">リストに追加するテキストを指定してください。</p>
      }
    </>
  );
}

// カテゴリー選択のセレクトボックス
function TodoFormCategorySelect({ categoryList, inputSelect, setInputSelect }: { categoryList: Category[], inputSelect: number, setInputSelect: (value: number) => void }) {
  return (
    <>
      {/* e.target.valueはHTMLの仕様で文字列のため、parseInt(e.target.value)にして数値にする */}
      <select name="category" id="category-select" className="todo-form-select" value={inputSelect}
        onChange={(e) => setInputSelect(parseInt(e.target.value))}>
        {categoryList.map(ct =>
          <option value={ct.id} key={ct.id} > {ct.name}</option>
        )}
      </select >
    </>
  );
}

// カテゴリーを追加するボタンの動作
// setter を props で渡すときに、prev => を使う時はDispatchが必要。
// DispatchとSetStateActionは、ReactのuseStateでstateを更新する関数の型を定義するためのもの。
// これを使うことで、setCategoryListの引数が正しい型であることをTypeScriptに伝えることができる。
async function AddCategory(setCategoryList: Dispatch<SetStateAction<Category[]>>, setInputSelect: (value: number) => void) {
  const userInput = prompt("カテゴリー名を入力してください");
  if (userInput) {
    // Supabase が ID を採番してくれるので不要になったローカルでのID計算はコメントアウト  
    // let maxId = categoryList.length > 0 ? Math.max(...categoryList.map(todo => todo.id)) : 0;

    const data = await insertCategory(userInput);
    if (!data) return;// モデル関数を呼んだ後は必ずエラーチェックをする習慣をつける。失敗したらnullが返るので、if (!data) return; でそこで処理を終える。
    setCategoryList(prev => [...prev, data]);
    setInputSelect(data.id);
    return data.id;
  }
}

// Todoの内容を追加する関数
// supbaseを利用するのでsyncに変更が必要
async function addTodoContent(todos: Todo[], setTodos: (value: Todo[]) => void, inputText: string, setInputText: (value: string) => void, inputSelect: number, setInputError: (value: number) => void) {
  if (inputText === "") {
    setInputError(1);
    return;
  } else {
    setInputError(0);

    // Supabaseに追加する前のローカルでのID計算は不要になったのでコメントアウト
    // Math.max()は最大値を返す
    // Math.max() は空の配列を渡すとエラーになるので、todosが空の場合は0をセットする
    // let maxId = todos.length > 0 ? Math.max(...todos.map(todo => todo.id)) : 0;
    // map ではなく、スプレッド構文で新しいオブジェクトを追加する形することで、後ろに追加される
    //setTodos([...todos,{ id: maxId + 1,name: inputText,category: inputSelect,isCheck: false }]);


    // SupabaseにTodoを追加する
    const data = await insertTodo(inputText, inputSelect);
    if (!data) return; // モデル関数を呼んだ後は必ずエラーチェックをする習慣をつける。失敗したらnullが返るので、if (!data) return; でそこで処理を終える。
    // Supabaseから返ってきたデータをstateに追加する
    //setTodos(data)だと既存のTodoが全部消えて新しい1件だけになってしまう
    // 既存のTodoに追加する
    setTodos([...todos, data]);
    setInputText("");
    return data.id;
  }
}

